import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {combineLatest, finalize, Subject} from "rxjs";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {BuilderV2Service} from "../../../../builder-v2.service";
import {Config} from "../../../../../config/config";
import {filter, first, takeUntil, tap} from "rxjs/operators";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {AiBuilderService} from "../../../../../websites/website/ai-builder/service/ai-builder.service";
import {ReCaptchaV3Service} from "ngx-captcha";
import {
  AiAssistantFormGroup
} from "../../../../../components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-form-data";
import {
  AiTemplateInputParams
} from "../../../../../ai-assistant/components/ai-assistant-templates/models/ai-template-params.model";
import * as fromStore from "../../../../../store";
import {Store} from "@ngrx/store";
import {UserService} from "../../../../../user/user.service";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {User} from "@sentry/browser";
import {BuilderStepsEnum, BuilderTourEnum} from "../../../../builder-tour.enum";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {ActivatedRoute, Router} from "@angular/router";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {TriggersService} from "../../../../services/triggers.service";
import {VibeCodingFlowService} from "../../../../../shared/services/vibe-coding-flow.service";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";

@Component({
  selector: 'db-create-company-info',
  templateUrl: './create-company-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './create-company-info.component.scss',
  standalone: false
})
export class CreateCompanyInfoComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('companyDescription') companyDescriptionEl: ElementRef;
  @ViewChild('companyName') companyNameEl: ElementRef;
  @ViewChild('descInput') descInput!: ElementRef<HTMLTextAreaElement>;

  @Input() fromVibeCoding: boolean = false;
  @Input() isEcommerce = false;
  @Input() oldFormData: Record<string, string | number | boolean | undefined> = {};
  @Input() formGroupData: AiAssistantFormGroup;
  @Input() domainId: number;
  @Input() popupMode: boolean;
  @Input() isBusinessType: boolean;
  @Input() user: User;
  @Input() uniqueId: string;
  @Output() back = new EventEmitter<boolean>();
  @Output() onMobileGenerate = new EventEmitter<boolean>();
  showFigmaLink = signal(false);

  public outlineFormGroup: FormGroup;
  public formSubmitted = false;
  public isMobile: boolean;
  public loading: boolean;
  public serverErrors: any;
  public showLoading: boolean = false;
  public showDomainInput: string = '';
  public domainInfo: string = '';
  public domainNameKey: string = 'domain_name';
  public hasFilledDomainName: boolean;
  public desiredDomainName: string = '';
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public updateLocalStorage: boolean = false;
  public userLocalStorage: any;
  public vibeCodingFlow: any;
  public generatedServices: any = null;
  public TypeUtils = TypeUtils;
  closeTooltip = signal(false);
  fileInput!: ElementRef<HTMLInputElement>;


  get initialDescription(): string {
    return this.companyDescriptionEl?.nativeElement?.value || '';
  }

  @HostBinding('class.db-vibe-coding') get applyVibeCodingClass() {
    return this.fromVibeCoding;
  }

  @HostBinding('class.db-mobile') get applyMobileClass() {
    return this.isMobile && !this.user && !this.popupMode;
  }

  constructor(
    private _fb: FormBuilder,
    public builderInteractionsService: BuilderSectionsInteractionService,
    private _builderV2Service: BuilderV2Service,
    private _builderService: AiBuilderService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private _store: Store<fromStore.DbStoreState>,
    private _userService: UserService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _builderHistoryService: BuilderHistoryService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _cdr: ChangeDetectorRef,
    private _builderTriggerService: TriggersService,
    private _vibeCodingFlowService: VibeCodingFlowService,
    private _commonDataService: CommonBuilderDataService
  ) {
    this.isEcommerce = this.builderInteractionsService.getWebsiteTypeMobile() === 'ecommerce' || this.builderInteractionsService.websiteType.value === 'ecommerce';
    this.uniqueId = this._builderHistoryService.getUniqueId();
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this.isMobile && this.checkMobileData();
    this.vibeCodingFlow = this._vibeCodingFlowService.isCombinedOutline();
    this._formGroupValueChanges();
    if (this.isMobile) {
      this.builderInteractionsService.setBackAction(() => {
        if (!this.popupMode && !this.user) {
          const data = {formData: this.outlineFormGroup?.value};
          this._builderHistoryService.saveFormData(data);
          this._router.navigate([`../v2-ai-website-building`], {
            relativeTo: this._route,
            queryParams: {websiteType: this.isEcommerce ? 'ecommerce' : 'business'}
          });
        } else {
          this.back.emit();
        }
      });
    }

    if (this.fromVibeCoding && !this.outlineFormGroup) {
      this.outlineFormGroup = this._fb.group({
        business_description: ['', [Validators.required, Validators.minLength(10)]]
      });

      this.outlineFormGroup.valueChanges.subscribe(() => {
        this.builderInteractionsService.disableNextButton.next(this.outlineFormGroup?.invalid || false);
      });
    }
  }

  checkMobileData() {
    const state = this._builderHistoryService.getFormData();

    if (state) {
      this.oldFormData = {...state.oldFormData, ...state.formData};
      this.formGroupData = state.formGroupData;
      this.isBusinessType = state.isBusinessType;
      this.builderInteractionsService.loading.next(false);
    }
    if (this.formGroupData && this.formGroupData.inputs) {
      this.createInputs(this.formGroupData.inputs);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formGroupData'] && changes['formGroupData'].currentValue && changes['formGroupData'].currentValue.inputs) {
      this.formGroupData = changes['formGroupData'].currentValue;

      if (localStorage.getItem('dbOnboardingDomainName') && localStorage.getItem('dbFromDomains')) {
        this.hasFilledDomainName = true;
      }
      this.createInputs(changes['formGroupData'].currentValue.inputs);
    }
  }

  ecommerceNext() {
    this._onboardingDomainName();
  }

  onGenerate() {
    // Get fresh vibeCodingFlow value at generation time
    const vibeCodingFlow = this._vibeCodingFlowService.isCombinedOutline();
    this._commonDataService.combinedOutline = vibeCodingFlow;

    this.builderInteractionsService.loading.next(true);

    if (this.outlineFormGroup?.invalid) {
      this.builderInteractionsService.loading.next(false);
      this.outlineFormGroup.markAllAsTouched();
      return;
    }
    const formValue = this.fromVibeCoding ? this.builderInteractionsService.siteInfo.value : this._getFormValue();
    this._onboardingDomainName();
    this.builderInteractionsService.siteInfo.next({
      ...formValue,
      domain_name: this.fromVibeCoding ? '' : this.outlineFormGroup.get(this.domainNameKey).value
    });
    if (this.isMobile) {
      const data = {
        formData: this.fromVibeCoding ? this.builderInteractionsService.siteInfo.value : this.outlineFormGroup?.value,
        siteInfo: this._getFormValue()
      };
      this._builderHistoryService.saveFormData(data);
      const state = this._builderHistoryService.getFormData();
      if (this.isEcommerce) {
        !this.user ? this._router.navigate(['../fill-products'], {
          state: {
            v2Data: state,
            uniqueId: this.uniqueId
          }, relativeTo: this._route
        }) : this._builderHistoryService.builderStep.next(BuilderStepsEnum.PRODUCT_TYPE);
      } else {
        this.user ? this.onMobileGenerate.emit() : this._router.navigate(['../mobile-sign-up'], {
          state: {
            v2Data: state,
            uniqueId: this.uniqueId
          }, relativeTo: this._route
        });
      }
      this.user && this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    } else {
      this._builderHistoryService.tourStep.next(BuilderTourEnum.START);
      this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
      const {initial_description: _, ...outlineFormValue} = formValue;
      if (vibeCodingFlow) {
        this._builderV2Service.aiGenerateOutline(outlineFormValue, this.domainId, this.uniqueId)
          .pipe(tap(value => {
            if (value) {
            }
          })).subscribe();
        return;
      }
      this._builderTriggerService.getPageMetas.next();
      combineLatest([
        this._builderV2Service.aiGenerateOutline(outlineFormValue, this.domainId, this.uniqueId)
          .pipe(tap(value => {
            if (value) {
            }
          })),
        this._builderV2Service.aiGenerateStyles({...outlineFormValue}, this.domainId, this.uniqueId)
      ]).subscribe(([outline, styles]) => {
        if ((styles && !styles.success) || (outline && !outline.success)) {
          NotifierComponent.notify(new Notification(outline?.message || styles?.message, NotificationType.DANGER, true));
        }
      });
    }
  }



  public domainShow(answer): void {
    this.showDomainInput = answer;
    this.builderInteractionsService.disableNextButton.next(this.outlineFormGroup.invalid || this.showDomainInput === '');
  }

  public ngOnDestroy(): void {
    this.removeEventHandlers();
    this.builderInteractionsService.vibeCodingLoading.next(false);
  }

  public generateDescription(text, fieldName): void {

    this.builderInteractionsService.generateDescriptionLoading.next(true);
    this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
      const businessType = this.outlineFormGroup.get('business_type') && this.outlineFormGroup.get('business_type').value || (this.oldFormData['business_type']);
      const language = 'english';
      this.builderInteractionsService.siteInfo.value && this.builderInteractionsService.siteInfo.next({
        ...this.builderInteractionsService.siteInfo.value,
        initial_description: text
      });
      this._builderService.generateDescription(text, businessType, token, !!this.isEcommerce, language)
        .pipe(
          filter((response: DbResponse): boolean => !!response),
          first(),
          takeUntil(this._destroyStream$),
          finalize(() => this.builderInteractionsService.generateDescriptionLoading.next(false))
        )
        .subscribe((response) => {
          if (response.success && response.data) {
            this.generatedServices = response.data;
            response.data && this.outlineFormGroup && this.outlineFormGroup.addControl('service_generated', new FormControl(JSON.stringify(response.data)));
            this.outlineFormGroup && this.outlineFormGroup.addControl('enhanced_description', new FormControl(response.data));
            this.outlineFormGroup.get(fieldName) && this.outlineFormGroup.controls[fieldName].setValue(response.data);
          } else {
            NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
          }
        });
    });
  }

  private _formGroupValueChanges(): void {
    this.outlineFormGroup && this.outlineFormGroup.valueChanges
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((changedValue: any) => {
        this.serverErrors = null;
        this.builderInteractionsService.disableNextButton.next(this.outlineFormGroup.invalid || this.showDomainInput === '');
      });
  }

  private createInputs(inputs: AiTemplateInputParams[]): void {
    this.formSubmitted = false;
    const controls = {};
    inputs.forEach(input => controls[input.name] = this.createFormControl(input));
    this.outlineFormGroup = new FormGroup(controls);
    if (this.hasFilledDomainName && this.outlineFormGroup.get('domain_name')) {
      this.showDomainInput = 'no';
      const domainNameFromLocalStorage = localStorage.getItem('dbOnboardingDomainName');
      if (domainNameFromLocalStorage !== null && domainNameFromLocalStorage !== undefined) {
        this.outlineFormGroup.get('domain_name').setValue(domainNameFromLocalStorage);
      }
    }
    this.builderInteractionsService.disableNextButton.next(this.outlineFormGroup?.invalid || this.showDomainInput === '');
  }

  private createFormControl(input: AiTemplateInputParams): FormControl {
    const validators = [];
    input.required && validators.push(Validators.required);
    input.min_character_limit && validators.push(Validators.minLength(input.min_character_limit));
    input.character_limit && validators.push(Validators.maxLength(input.character_limit));
    const value = this.oldFormData[input.name] || input.default || '';
    this.showDomainInput = TypeUtils.isDefined(this.oldFormData['domain_name']) ? this.oldFormData['domain_name'] === '' ? 'yes' : 'no' : '';
    return new FormControl(value, validators);
  }

  private _getFormValue() {
    return {
      'business_description': this.companyDescriptionEl.nativeElement.value as string,
      'business_type': this.oldFormData['business_type'] as string,
      'initial_description': this.oldFormData['initial_description'] as string,
      'business_name': this.companyNameEl.nativeElement.value as string
    };
  }

  private _onboardingDomainName(): void {
    if (this.domainNameKey in this.outlineFormGroup.value) {
      if (this.showDomainInput === 'no' && this.outlineFormGroup.get('domain_name').value !== '') {
        const domainNameValue = this.outlineFormGroup.get('domain_name').value;
        this._checkSuggestedDomain(domainNameValue);
      } else {
        this._generateDomainInfo(this.outlineFormGroup.get('domain_name').value);
      }
    }
  }

  private _generateDomainInfo(domainName): void {
    const domainInfo = {
      "want_domain": this.showDomainInput === 'no' ? 1 : 0,
      "domain_name": domainName || ""
    };
    this.domainInfo = JSON.stringify(domainInfo);
    localStorage.setItem('dbOnboardingDomainInfo', this.domainInfo);
    if (this.isMobile) {
      const mobileDomainInfo = {mobileDomainInfo: this.domainInfo};
      this._builderHistoryService.saveFormData(mobileDomainInfo);
    }
  }

  private _checkSuggestedDomain(domainName) {
    localStorage.setItem('dbOnboardingDomainNameInitial', domainName);
  }

  private removeEventHandlers(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  addFigma() {
    this.showFigmaLink.set(true);
    this.closeTooltip.set(true);
    setTimeout(() => {
      this.closeTooltip.set(false);
    }, 200)
  }

  openFileDialog() {
    this.fileInput.nativeElement.click()
  }

  onKeyDown(e: KeyboardEvent) {
    if ((e.key === 'Backspace' || e.key === 'Delete') && !this.outlineFormGroup.get('business_description').value) {
      this.showFigmaLink.set(false);
      this.builderInteractionsService.vibeCodingFigma = null;
    }
  }
}
